import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, FileText, Users, BarChart3, ExternalLink, Copy, CheckCircle, Clock, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { assessmentAPI, questionnaireAPI } from '../../services/api';
import { format } from 'date-fns';
import Button from '../common/Button';

const AssessmentDetails = () => {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [assignmentInfo, setAssignmentInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  // Helper function for role-aware navigation
  const navigateToRoleDashboard = () => {
    const isLeader = window.location.pathname.includes('/leaders/');
    const dashboardPath = isLeader ? '/gps/leaders/dashboard' : '/gps/team/dashboard';
    navigate(dashboardPath);
  };

  useEffect(() => {
    loadAssessmentDetails();
  }, [assessmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Categories start collapsed by default - no auto-expansion

  const loadAssessmentDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 AssessmentDetails Debug:', {
        assessmentId,
        assessmentIdType: typeof assessmentId,
        assessmentIdLength: assessmentId?.length,
        currentURL: window.location.href,
        pathname: window.location.pathname
      });

      if (!assessmentId) {
        throw new Error('Assessment ID is missing from URL');
      }

      const data = await assessmentAPI.getAssessmentDetails(assessmentId);
      setAssessment(data.assessment);
      setQuestionnaires(data.questionnaires || []);
      
      // Load assignment info to get team size
      await loadAssignmentInfo();
    } catch (error) {
      console.error('Failed to load assessment details:', error);
      setError(error.message || 'Failed to load assessment details');
    } finally {
      setLoading(false);
    }
  };

  const loadAssignmentInfo = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_GPS_API_URL}/api/assessments/${assessmentId}/assignments`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        const assignment = data.assignments?.[0] || null;
        setAssignmentInfo(assignment);
        
        // If this is a vibe check assessment (has assignment), load pillar schedules
        if (assignment) {
          await loadPillarSchedules(assignment.AssignmentId);
        }
      }
    } catch (error) {
      console.error('Failed to load assignment info:', error);
    }
  };

  const loadPillarSchedules = async (assignmentId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_GPS_API_URL}/api/team-member/pillar-schedule?assignment_id=${assignmentId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        // Convert pillar schedules to questionnaire format
        const pillarQuestionnaires = (data.pillars || []).map(pillar => ({
          QuestionnaireId: `${assignmentId}-pillar-${pillar.PillarNumber}`,
          BenchmarkArea: pillar.PillarName,
          Area: 'GPS Vibe Check',
          Status: pillar.Status,
          response_count: pillar.CompletionCount || 0,
          CreatedDate: pillar.CreatedDate,
          ExpirationDate: pillar.EndDate,
          OriginalScore: null
        }));
        
        // If we have pillar schedules, use them instead of regular questionnaires
        if (pillarQuestionnaires.length > 0) {
          setQuestionnaires(pillarQuestionnaires);
        }
      }
    } catch (error) {
      console.error('Failed to load pillar schedules:', error);
    }
  };

  const copyQuestionnaireUrl = async (questionnaireId) => {
    const url = `${window.location.origin}/q/${questionnaireId}`;

    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
        setCopiedUrl(questionnaireId);
        setTimeout(() => setCopiedUrl(null), 2000);
        return;
      }

      // Fallback for older browsers or non-secure contexts
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (successful) {
        setCopiedUrl(questionnaireId);
        setTimeout(() => setCopiedUrl(null), 2000);
      } else {
        throw new Error('Copy command failed');
      }
    } catch (error) {
      console.error('Failed to copy URL:', error);

      // Final fallback - show the URL in an alert or prompt
      const userAgent = navigator.userAgent.toLowerCase();
      if (userAgent.includes('mobile') || userAgent.includes('android') || userAgent.includes('iphone')) {
        // On mobile, show an alert with the URL
        alert(`Please copy this URL manually:\n\n${url}`);
      } else {
        // On desktop, try to select the URL in a prompt
        prompt('Copy this URL:', url);
      }
    }
  };

  const collectResponses = async (questionnaireId) => {
    try {
      await questionnaireAPI.collectResponses(questionnaireId);
      // Refresh the data
      loadAssessmentDetails();
    } catch (error) {
      console.error('Failed to collect responses:', error);
    }
  };

  const triggerAnalysis = async (questionnaireId) => {
    console.log('🔍 Triggering analysis for questionnaire:', questionnaireId);
    try {
      console.log('📡 Making API call to trigger analysis...');
      const result = await assessmentAPI.triggerAnalysis(assessmentId, questionnaireId);
      console.log('✅ Analysis API call successful:', result);
      alert('Analysis started! Check back in a few minutes for results.');
      // Reload assessment details to refresh status
      await loadAssessmentDetails();
    } catch (error) {
      console.error('❌ Failed to trigger analysis:', error);
      alert(`Failed to start analysis: ${error.message || 'Unknown error'}`);
    }
  };

  const viewResults = async (questionnaireId) => {
    console.log('📊 Viewing results for questionnaire:', questionnaireId);
    try {
      setResultsLoading(true);
      console.log('📡 Making API call to get analysis results...');
      const results = await questionnaireAPI.getAnalysisResults(questionnaireId);
      console.log('✅ Analysis results received:', results);
      console.log('🔄 Setting analysis results state...');
      setAnalysisResults(results);
      console.log('🔄 Setting show results to true...');
      setShowResults(true);
      console.log('✅ Modal should now be visible');
    } catch (error) {
      console.error('❌ Failed to load results:', error);
      console.error('❌ Error details:', error.response?.data || error.message);
      alert(`Failed to load results: ${error.response?.data?.error || error.message || 'Unknown error'}`);
    } finally {
      console.log('🔄 Setting results loading to false...');
      setResultsLoading(false);
    }
  };

  const generateFollowup = async (questionnaireId) => {
    console.log('📝 Generating follow-up questionnaire for:', questionnaireId);
    try {
      await questionnaireAPI.generateFollowupQuestionnaire(questionnaireId);
      alert('Follow-up questionnaire generated successfully! Check your assessments for the new questionnaire.');
      // Reload assessment details to show new questionnaire
      await loadAssessmentDetails();
    } catch (error) {
      console.error('❌ Failed to generate follow-up:', error);
      alert(`Failed to generate follow-up questionnaire: ${error.message || 'Unknown error'}`);
    }
  };

  // Group questionnaires by BenchmarkArea (pillar/category)
  const groupQuestionnairesByCategory = (questionnaires) => {
    const grouped = questionnaires.reduce((acc, questionnaire) => {
      let category = questionnaire.BenchmarkArea || 'Other';

      // Normalize category name by removing follow-up suffixes
      category = category
        .replace(/\s*-\s*Follow\s*-?\s*Up/i, '')
        .replace(/\s*Follow\s*-?\s*Up/i, '')
        .replace(/\s*\(Follow-up\)/i, '')
        .trim();

      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(questionnaire);
      return acc;
    }, {});

    // Sort questionnaires within each category - original first, then follow-ups by date
    Object.keys(grouped).forEach(category => {
      grouped[category].sort((a, b) => {
        const aIsFollowUp = (a.QuestionnaireId && a.QuestionnaireId.includes('followup')) ||
          (a.BenchmarkArea && (
            a.BenchmarkArea.includes('Follow-Up') ||
            a.BenchmarkArea.includes('Follow Up') ||
            a.BenchmarkArea.includes('(Follow-up)')
          ));
        const bIsFollowUp = (b.QuestionnaireId && b.QuestionnaireId.includes('followup')) ||
          (b.BenchmarkArea && (
            b.BenchmarkArea.includes('Follow-Up') ||
            b.BenchmarkArea.includes('Follow Up') ||
            b.BenchmarkArea.includes('(Follow-up)')
          ));

        // Original questionnaires first
        if (!aIsFollowUp && bIsFollowUp) return -1;
        if (aIsFollowUp && !bIsFollowUp) return 1;

        // Within same type, sort by creation date (newest first)
        const dateA = new Date(a.CreatedDate || 0);
        const dateB = new Date(b.CreatedDate || 0);
        return dateB - dateA;
      });
    });

    return grouped;
  };

  // Calculate category statistics
  const getCategoryStats = (categoryQuestionnaires) => {
    const totalQuestionnaires = categoryQuestionnaires.length;
    const totalResponses = categoryQuestionnaires.reduce((sum, q) => sum + (q.response_count || 0), 0);
    const activeCount = categoryQuestionnaires.filter(q => q.Status === 'ACTIVE').length;
    const completedCount = categoryQuestionnaires.filter(q => ['ANALYZED', 'COMPLETED'].includes(q.Status)).length;
    
    // Calculate completion rate based on responses vs expected responses
    // For vibe check assessments, use response count / team size
    // For old assessments, use completed questionnaires / total questionnaires
    let completionRate = 0;
    if (assignmentInfo?.TotalTeamMembers) {
      // Vibe check assessment - calculate based on total responses vs total expected
      const totalExpectedResponses = totalQuestionnaires * assignmentInfo.TotalTeamMembers;
      completionRate = totalExpectedResponses > 0 ? Math.round((totalResponses / totalExpectedResponses) * 100) : 0;
    } else {
      // Old assessment - calculate based on completed questionnaires
      completionRate = totalQuestionnaires > 0 ? Math.round((completedCount / totalQuestionnaires) * 100) : 0;
    }

    // Count original vs follow-up questionnaires
    const followUpCount = categoryQuestionnaires.filter(q =>
      (q.QuestionnaireId && q.QuestionnaireId.includes('followup')) ||
      (q.BenchmarkArea && (
        q.BenchmarkArea.includes('Follow-Up') ||
        q.BenchmarkArea.includes('Follow Up') ||
        q.BenchmarkArea.includes('(Follow-up)')
      ))
    ).length;
    const originalCount = totalQuestionnaires - followUpCount;

    return {
      totalQuestionnaires,
      originalCount,
      followUpCount,
      totalResponses,
      activeCount,
      completedCount,
      completionRate
    };
  };

  // Calculate individual questionnaire completion percentage based on actual team size
  const getQuestionnaireCompletionRate = (questionnaire) => {
    const targetResponses = assignmentInfo?.TotalTeamMembers || 10; // Use actual team size or fallback to 10
    const currentResponses = questionnaire.response_count || 0;
    return Math.min(Math.round((currentResponses / targetResponses) * 100), 100);
  };

  // Toggle category expansion
  const toggleCategory = (category) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  // Expand or collapse all categories
  const toggleAllCategories = () => {
    const categories = Object.keys(groupQuestionnairesByCategory(questionnaires));
    const allExpanded = categories.every(cat => expandedCategories.has(cat));

    if (allExpanded) {
      setExpandedCategories(new Set());
    } else {
      setExpandedCategories(new Set(categories));
    }
  };

  // Removed unused getStatusIcon function

  const getStatusClass = (status) => {
    const classes = {
      'ACTIVE': 'badge-green',
      'PROCESSING': 'badge-yellow',
      'COLLECTED': 'badge-blue',
      'ANALYZING': 'badge-orange',
      'ANALYZED': 'badge-purple',
      'ERROR': 'badge-red',
      'COMPLETED': 'badge-gray'
    };
    return classes[status] || 'badge-gray';
  };

  if (loading) {
    return (
      <div className="assessment-details-container">
        <div className="assessment-header">
          <div className="container">
            <button className="assessment-back-button" onClick={navigateToRoleDashboard}>
              ← Back to Dashboard
            </button>
          </div>
        </div>
        <div className="loading-container">
          <div className="spinner"></div>
          <p className="loading-text">Loading assessment details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="assessment-details-container">
        <div className="assessment-header">
          <div className="container">
            <button className="assessment-back-button" onClick={navigateToRoleDashboard}>
              ← Back to Dashboard
            </button>
          </div>
        </div>
        <div className="assessment-content">
          <div className="stats-card text-center" style={{ maxWidth: '600px', margin: '0 auto' }}>
            <AlertCircle className="mx-auto h-16 w-16 mb-6" style={{ color: 'var(--error-600)' }} />
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--primary-800)' }}>Error Loading Assessment</h2>
            <p className="text-lg mb-8" style={{ color: 'var(--primary-700)' }}>{error}</p>
            <button className="questionnaire-button questionnaire-button-accent" onClick={loadAssessmentDetails}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="assessment-details-container">
      {/* Header */}
      <div className="assessment-header">
        <div className="container">
          <button
            className="assessment-back-button"
            onClick={navigateToRoleDashboard}
          >
            ← Back to Dashboard
          </button>

          <h1 className="assessment-title">
            {assessment?.SiteName} Assessment
          </h1>

          <p className="assessment-subtitle">
            {assessment?.Year} • Created {assessment?.CreatedDate && format(new Date(assessment.CreatedDate), 'MMM dd, yyyy')}
          </p>

          <div className={`assessment-status-badge badge-${getStatusClass(assessment?.Status).replace('badge-', '')}`}>
            {assessment?.Status}
          </div>
        </div>
      </div>

      <div className="assessment-content">
        {/* Statistics Grid */}
        <div className="stats-grid">
          <div className="stats-card">
            <div
              className="stats-card-icon"
              style={{ background: 'var(--primary-100)', color: 'var(--primary-600)' }}
            >
              <FileText size={24} />
            </div>
            <div className="stats-card-value">
              {questionnaires.length}
            </div>
            <div className="stats-card-label">
              Questionnaires
            </div>
          </div>

          <div className="stats-card">
            <div
              className="stats-card-icon"
              style={{ background: 'var(--success-100)', color: 'var(--success-600)' }}
            >
              <Users size={24} />
            </div>
            <div className="stats-card-value">
              {questionnaires.reduce((total, q) => total + (q.response_count || 0), 0)}
            </div>
            <div className="stats-card-label">
              Total Responses
            </div>
          </div>

          <div className="stats-card">
            <div
              className="stats-card-icon"
              style={{ background: 'var(--accent-100)', color: 'var(--accent-600)' }}
            >
              <BarChart3 size={24} />
            </div>
            <div className="stats-card-value">
              {assessment?.TotalBenchmarks || 0}
            </div>
            <div className="stats-card-label">
              Benchmarks
            </div>
          </div>
        </div>

        {/* Questionnaires Section - Grouped by Category */}
        <div className="questionnaires-section">
          <div className="questionnaires-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
              <h2 className="questionnaires-title">Questionnaires by Pillar</h2>
              {questionnaires.length > 0 && (
                <button
                  onClick={toggleAllCategories}
                  className="questionnaire-button questionnaire-button-secondary"
                  style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                >
                  {Object.keys(groupQuestionnairesByCategory(questionnaires)).every(cat => expandedCategories.has(cat))
                    ? 'Collapse All'
                    : 'Expand All'
                  }
                </button>
              )}
            </div>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
              Click on a pillar to expand and view questionnaires, follow-ups, and completion details
            </p>
          </div>

          <div className="questionnaires-content">
            {questionnaires.length === 0 ? (
              <div className="empty-state">
                <FileText className="empty-state-icon" />
                <h3 className="empty-state-title">No Questionnaires Found</h3>
                <p className="empty-state-description">
                  No questionnaires found for this assessment.
                </p>
              </div>
            ) : (
              <div className="category-groups">
                {Object.entries(groupQuestionnairesByCategory(questionnaires)).map(([category, categoryQuestionnaires]) => {
                  const stats = getCategoryStats(categoryQuestionnaires);
                  const isExpanded = expandedCategories.has(category);

                  return (
                    <CategoryCard
                      key={category}
                      category={category}
                      questionnaires={categoryQuestionnaires}
                      stats={stats}
                      isExpanded={isExpanded}
                      onToggle={() => toggleCategory(category)}
                      onCopyUrl={copyQuestionnaireUrl}
                      onCollectResponses={collectResponses}
                      onTriggerAnalysis={triggerAnalysis}
                      onViewResults={viewResults}
                      onGenerateFollowup={generateFollowup}
                      copiedUrl={copiedUrl}
                      getCompletionRate={getQuestionnaireCompletionRate}
                      assignmentInfo={assignmentInfo}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Debug: Test Modal Button */}
      <div style={{ position: 'fixed', top: '10px', right: '10px', zIndex: 9999 }}>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            console.log('🧪 Test button clicked - setting modal to show');
            setAnalysisResults({
              questionnaire_id: 'test',
              site_name: 'Test Site',
              benchmark_area: 'Test Area',
              year: '2024',
              analysis_content: 'Test analysis content'
            });
            setShowResults(true);
          }}
        >
          Test Modal
        </Button>
      </div>

      {/* Analysis Results Modal */}

      {showResults && (
        <div
          className="modal-overlay"
          onClick={() => setShowResults(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '0.5rem',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
              maxWidth: '90vw',
              maxHeight: '90vh',
              width: '800px',
              display: 'flex',
              flexDirection: 'column',
              zIndex: 10000,
              position: 'relative'
            }}
          >
            <div className="modal-header">
              <h2>Analysis Results</h2>
              <button className="modal-close" onClick={() => setShowResults(false)}>×</button>
            </div>
            <div className="modal-body">
              {resultsLoading ? (
                <div className="text-center">
                  <div className="spinner"></div>
                  <p>Loading analysis results...</p>
                </div>
              ) : analysisResults ? (
                <div>
                  <div className="results-header">
                    <h3>
                      {analysisResults.questionnaire?.site_name || analysisResults.site_name || 'Unknown Site'} - {analysisResults.questionnaire?.benchmark_area || analysisResults.benchmark_area || 'Unknown Area'}
                    </h3>
                    <p>Year: {analysisResults.questionnaire?.year || analysisResults.year || 'Unknown'}</p>
                    <p>Total Responses: {analysisResults.analysis_metadata?.total_responses || analysisResults.total_responses || 0}</p>
                    <p>Overall Average: {analysisResults.analysis_metadata?.overall_average || analysisResults.overall_average || 'N/A'}/5</p>
                    {(analysisResults.analysis_metadata?.completed_at || analysisResults.analysis_completed_at) && (
                      <p>Completed: {new Date(analysisResults.analysis_metadata?.completed_at || analysisResults.analysis_completed_at).toLocaleString()}</p>
                    )}
                  </div>
                  <div className="results-content">
                    {analysisResults.analysis_content ? (
                      <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem', lineHeight: '1.5' }}>
                        {analysisResults.analysis_content}
                      </pre>
                    ) : (
                      <div>
                        <p><strong>Analysis results are not yet available.</strong></p>
                        <p>This could mean:</p>
                        <ul style={{ marginLeft: '1rem', marginTop: '0.5rem' }}>
                          <li>The analysis is still processing</li>
                          <li>The analysis file hasn't been generated yet</li>
                          <li>The backend API needs to be updated</li>
                        </ul>
                        <p style={{ marginTop: '1rem' }}>
                          <strong>Questionnaire Details:</strong>
                        </p>
                        <p>ID: {analysisResults.questionnaire_id}</p>
                        <p>Questions: {analysisResults.questions?.length || 0}</p>
                        <p>Status: {analysisResults.Status || 'Unknown'}</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <p>No results available</p>
              )}
            </div>
            <div className="modal-footer">
              <Button variant="outline" onClick={() => setShowResults(false)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CategoryCard = ({
  category,
  questionnaires,
  stats,
  isExpanded,
  onToggle,
  onCopyUrl,
  onCollectResponses,
  onTriggerAnalysis,
  onViewResults,
  onGenerateFollowup,
  copiedUrl,
  getCompletionRate,
  assignmentInfo
}) => {
  const getCategoryColor = (category, stats) => {
    // Base colors for different pillars/categories (reserved for future use)
    // const baseColors = {
    //   'We Drive Flow': 'primary',
    //   'We Develop People': 'success', 
    //   'We Improve Everything': 'accent',
    //   'We Respect Everyone': 'warning',
    //   'We Deliver Results': 'info',
    //   'Other': 'neutral'
    // };

    // const baseColor = baseColors[category] || 'neutral'; // Reserved for future use

    // Determine color based on completion status
    if (stats.completionRate === 0) {
      // Grey for no completed questionnaires
      return {
        bg: 'var(--neutral-100)',
        border: 'var(--neutral-300)',
        text: 'var(--neutral-700)'
      };
    } else if (stats.completionRate === 100) {
      // Green for all questionnaires completed
      return {
        bg: 'var(--success-100)',
        border: 'var(--success-300)',
        text: 'var(--success-700)'
      };
    } else {
      // Blue for partially completed
      return {
        bg: 'var(--primary-100)',
        border: 'var(--primary-300)',
        text: 'var(--primary-700)'
      };
    }
  };

  const categoryColor = getCategoryColor(category, stats);

  return (
    <div className="category-card" style={{ marginBottom: '1.5rem' }}>
      {/* Category Header - Always Visible */}
      <div
        className="category-header"
        onClick={onToggle}
        style={{
          background: categoryColor.bg,
          border: `1px solid ${categoryColor.border}`,
          borderRadius: '12px',
          padding: '1.5rem',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: isExpanded ? '1rem' : '0'
        }}
      >
        <div className="category-info" style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h3 style={{
              color: categoryColor.text,
              fontSize: '1.25rem',
              fontWeight: '700',
              margin: 0,
              marginRight: '1rem'
            }}>
              {category}
            </h3>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <span style={{
                background: categoryColor.text,
                color: 'white',
                padding: '0.25rem 0.75rem',
                borderRadius: '12px',
                fontSize: '0.75rem',
                fontWeight: '600'
              }}>
                {stats.originalCount} original
              </span>
              {stats.followUpCount > 0 && (
                <span style={{
                  background: 'var(--accent-500)',
                  color: 'white',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}>
                  {stats.followUpCount} follow-up{stats.followUpCount !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Users size={16} style={{ color: categoryColor.text }} />
              <span style={{ color: categoryColor.text, fontSize: '0.875rem', fontWeight: '500' }}>
                {stats.totalResponses} responses
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <BarChart3 size={16} style={{ color: categoryColor.text }} />
              <span style={{ color: categoryColor.text, fontSize: '0.875rem', fontWeight: '500' }}>
                {stats.completionRate}% complete
              </span>
            </div>

            {stats.activeCount > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <CheckCircle size={16} style={{ color: categoryColor.text }} />
                <span style={{ color: categoryColor.text, fontSize: '0.875rem', fontWeight: '500' }}>
                  {stats.activeCount} active
                </span>
              </div>
            )}
          </div>
        </div>

        <div style={{ marginLeft: '1rem' }}>
          {isExpanded ? (
            <ChevronDown size={24} style={{ color: categoryColor.text }} />
          ) : (
            <ChevronRight size={24} style={{ color: categoryColor.text }} />
          )}
        </div>
      </div>

      {/* Expanded Content - Questionnaires List */}
      {isExpanded && (
        <div className="category-content" style={{
          background: 'white',
          border: '1px solid var(--neutral-200)',
          borderRadius: '12px',
          overflow: 'hidden'
        }}>
          {questionnaires.map((questionnaire, index) => {
            const isFollowUp = (questionnaire.QuestionnaireId && questionnaire.QuestionnaireId.includes('followup')) ||
              (questionnaire.BenchmarkArea && (
                questionnaire.BenchmarkArea.includes('Follow-Up') ||
                questionnaire.BenchmarkArea.includes('Follow Up') ||
                questionnaire.BenchmarkArea.includes('(Follow-up)')
              ));

            return (
              <div key={questionnaire.QuestionnaireId} style={{
                borderBottom: index < questionnaires.length - 1 ? '1px solid var(--neutral-200)' : 'none',
                borderLeft: isFollowUp ? '3px solid var(--accent-400)' : 'none',
                background: isFollowUp ? 'var(--accent-50)' : 'transparent'
              }}>
                <QuestionnaireCard
                  questionnaire={questionnaire}
                  onCopyUrl={onCopyUrl}
                  onCollectResponses={onCollectResponses}
                  onTriggerAnalysis={onTriggerAnalysis}
                  onViewResults={onViewResults}
                  onGenerateFollowup={onGenerateFollowup}
                  copiedUrl={copiedUrl}
                  isInCategory={true}
                  getCompletionRate={getCompletionRate}
                  assignmentInfo={assignmentInfo}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const QuestionnaireCard = ({
  questionnaire,
  onCopyUrl,
  onCollectResponses,
  onTriggerAnalysis,
  onViewResults,
  onGenerateFollowup,
  copiedUrl,
  isInCategory = false,
  getCompletionRate,
  assignmentInfo
}) => {
  const getStatusIcon = (status) => {
    const iconStyle = { width: '1.5rem', height: '1.5rem' };
    switch (status) {
      case 'ACTIVE':
        return <CheckCircle style={{ ...iconStyle, color: 'var(--success-600)' }} />;
      case 'PROCESSING':
        return <Clock style={{ ...iconStyle, color: 'var(--warning-600)' }} />;
      case 'COLLECTED':
        return <BarChart3 style={{ ...iconStyle, color: 'var(--primary-600)' }} />;
      case 'ANALYZING':
        return <Clock style={{ ...iconStyle, color: 'var(--accent-600)' }} />;
      case 'ANALYZED':
        return <CheckCircle style={{ ...iconStyle, color: 'var(--accent-600)' }} />;
      case 'ERROR':
        return <AlertCircle style={{ ...iconStyle, color: 'var(--error-600)' }} />;
      default:
        return <AlertCircle style={{ ...iconStyle, color: 'var(--neutral-500)' }} />;
    }
  };

  const getStatusClass = (status) => {
    const classes = {
      'ACTIVE': 'badge-green',
      'PROCESSING': 'badge-yellow',
      'COLLECTED': 'badge-blue',
      'COMPLETED': 'badge-gray'
    };
    return classes[status] || 'badge-gray';
  };

  // Determine if this is a follow-up questionnaire
  const isFollowUp = (questionnaire.QuestionnaireId && questionnaire.QuestionnaireId.includes('followup')) ||
    (questionnaire.BenchmarkArea && (
      questionnaire.BenchmarkArea.includes('Follow-Up') ||
      questionnaire.BenchmarkArea.includes('Follow Up') ||
      questionnaire.BenchmarkArea.includes('(Follow-up)')
    ));

  return (
    <div className={isInCategory ? "questionnaire-item-in-category" : "questionnaire-item"} style={isInCategory ? {
      background: 'transparent',
      border: 'none',
      borderRadius: '0',
      padding: '1.5rem',
      margin: '0'
    } : {}}>
      <div className="questionnaire-header">
        <div className="questionnaire-info">
          <h3 className="questionnaire-title" style={isInCategory ? { fontSize: '1.125rem' } : {}}>
            {isInCategory ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {isFollowUp && (
                  <span style={{
                    color: 'var(--accent-600)',
                    fontSize: '1rem',
                    fontWeight: '400'
                  }}>
                    ↳
                  </span>
                )}
                <span>
                  {questionnaire.Area || questionnaire.BenchmarkArea || 'Assessment'}
                </span>
                {isFollowUp && (
                  <span style={{
                    background: 'var(--accent-100)',
                    color: 'var(--accent-700)',
                    fontSize: '0.75rem',
                    padding: '0.125rem 0.5rem',
                    borderRadius: '0.375rem',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.025em'
                  }}>
                    Follow-up
                  </span>
                )}
              </span>
            ) : (
              questionnaire.BenchmarkArea
            )}
          </h3>
          <p className="questionnaire-subtitle">
            {isInCategory ? (
              <>
                Created: {questionnaire.CreatedDate && format(new Date(questionnaire.CreatedDate), 'MMM dd, yyyy')}
                {questionnaire.OriginalScore && ` • Original Score: ${questionnaire.OriginalScore}`}
              </>
            ) : (
              `${questionnaire.Area} • Original Score: ${questionnaire.OriginalScore}`
            )}
          </p>
        </div>

        <div className="questionnaire-status">
          {getStatusIcon(questionnaire.Status)}
          <span className={`badge ${getStatusClass(questionnaire.Status)}`}>
            {questionnaire.Status}
          </span>
        </div>
      </div>

      <div className="questionnaire-meta">
        <div className="questionnaire-meta-item">
          <Users className="questionnaire-meta-icon" />
          <span>{questionnaire.response_count || 0}/{assignmentInfo?.TotalTeamMembers || 10} responses</span>
        </div>
        <div className="questionnaire-meta-item">
          <Calendar className="questionnaire-meta-icon" />
          <span>Expires {questionnaire.ExpirationDate && format(new Date(questionnaire.ExpirationDate), 'MMM dd')}</span>
        </div>
      </div>

      {/* Progress Bar for Individual Questionnaire */}
      {getCompletionRate && (
        <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)' }}>
              Response Progress
            </span>
            <span style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-primary)' }}>
              {getCompletionRate(questionnaire)}%
            </span>
          </div>
          <div style={{
            width: '100%',
            height: '8px',
            backgroundColor: 'var(--neutral-200)',
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${getCompletionRate(questionnaire)}%`,
              height: '100%',
              backgroundColor: getCompletionRate(questionnaire) === 100 ? 'var(--success-500)' :
                getCompletionRate(questionnaire) > 0 ? 'var(--primary-500)' : 'var(--neutral-400)',
              transition: 'width 0.3s ease, background-color 0.3s ease'
            }} />
          </div>
        </div>
      )}

      <div className="questionnaire-actions">
        {questionnaire.Status === 'ACTIVE' && (
          <>
            <button
              className={`questionnaire-button questionnaire-button-secondary ${copiedUrl === questionnaire.QuestionnaireId ? 'copied' : ''}`}
              onClick={() => onCopyUrl(questionnaire.QuestionnaireId)}
            >
              {copiedUrl === questionnaire.QuestionnaireId ? (
                <>
                  <CheckCircle className="questionnaire-button-icon" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="questionnaire-button-icon" />
                  Copy URL
                </>
              )}
            </button>
            <button
              className="questionnaire-button questionnaire-button-secondary"
              onClick={() => window.open(`/q/${questionnaire.QuestionnaireId}`, '_blank')}
            >
              <ExternalLink className="questionnaire-button-icon" />
              Preview
            </button>
            <button
              className="questionnaire-button questionnaire-button-primary"
              onClick={() => onCollectResponses(questionnaire.QuestionnaireId)}
            >
              Collect Responses
            </button>
          </>
        )}

        {questionnaire.Status === 'COLLECTED' && (
          <button
            className="questionnaire-button questionnaire-button-accent"
            onClick={() => onTriggerAnalysis(questionnaire.QuestionnaireId)}
          >
            <BarChart3 className="questionnaire-button-icon" />
            Analyze Responses
          </button>
        )}

        {questionnaire.Status === 'ANALYZING' && (
          <button
            className="questionnaire-button questionnaire-button-secondary"
            disabled
            style={{ opacity: 0.6, cursor: 'not-allowed' }}
          >
            <Clock className="questionnaire-button-icon" />
            Analyzing...
          </button>
        )}

        {questionnaire.Status === 'ANALYZED' && (
          <>
            <button
              className="questionnaire-button questionnaire-button-accent"
              onClick={() => onViewResults(questionnaire.QuestionnaireId)}
            >
              <CheckCircle className="questionnaire-button-icon" />
              View Results
            </button>
            <button
              className="questionnaire-button questionnaire-button-primary"
              onClick={() => onGenerateFollowup(questionnaire.QuestionnaireId)}
            >
              <FileText className="questionnaire-button-icon" />
              Generate Follow-up
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AssessmentDetails;